import axios, {
  type AxiosError,
  type AxiosRequestConfig,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from 'axios';

/**
 * The single axios instance every generated hook calls through (`apiFetch`). All cross-cutting HTTP
 * concerns live here so auth is applied in exactly one place:
 *   - `Authorization: Bearer <access>` from the in-memory token (via injected `getAccessToken`)
 *   - `withCredentials` so the httpOnly `rt` refresh cookie flows to `/v1/auth/refresh`
 *   - `X-CSRF-Token` on cookie-authenticated mutations (`/refresh`, `/logout`, `/change-password`)
 *   - transparent 401 -> refresh-once -> retry; on refresh failure, the host's `onAuthFailure` runs
 *
 * apps/web injects the auth bridge at startup via {@link configureApiClient} so this package has no
 * dependency on the app's store (keeps the contract package framework-agnostic and testable).
 */

/**
 * The offline outbox bridge (Phase 7, ADR-0018). When the device is offline and a *mutation* (a
 * non-GET write that carries an `Idempotency-Key`) cannot reach the network, the mutator hands the
 * request to this bridge to be persisted to an app-managed IndexedDB queue, then RESOLVES the call
 * (so the optimistic UI holds) instead of rejecting. A replay layer drains the queue on
 * reconnect/foreground. The contract package stays framework-agnostic: `apps/web` injects a concrete
 * IndexedDB-backed implementation via {@link configureOutbox}, exactly like the auth bridge.
 */
export interface OutboxBridge {
  /**
   * Persist a mutation that failed because the device is offline. Returns `true` if the request was
   * accepted into the queue (the mutator then resolves the original call); `false` to let the error
   * propagate as normal (e.g. the request wasn't queueable). Implementations MUST be idempotent on
   * the request's `Idempotency-Key` so a double-enqueue (e.g. React retry) never duplicates.
   */
  enqueue: (request: OutboxRequest) => Promise<boolean>;
}

/** A file part of a serialized multipart body: raw bytes + the metadata to rebuild the `File`. */
export interface OutboxFilePart {
  readonly __file: {
    readonly name: string;
    readonly type: string;
    readonly bytes: ArrayBuffer;
  };
}

/**
 * Cloneable stand-in for a multipart (FormData) body persisted in the outbox. FormData itself is not
 * structured-cloneable — IndexedDB `put` would throw `DataCloneError` and the enqueue would silently
 * fail — so the mutator serializes its entries to this shape (file parts as raw ArrayBuffer bytes,
 * which clone reliably in every engine) and the replay layer rebuilds a real FormData before
 * re-sending.
 */
export interface OutboxMultipartBody {
  readonly __multipart: ReadonlyArray<readonly [string, string | OutboxFilePart]>;
}

/** Read a file part's bytes; falls back to FileReader where `Blob.arrayBuffer` is missing (jsdom). */
function readFileBytes(file: File): Promise<ArrayBuffer> {
  if (typeof file.arrayBuffer === 'function') return file.arrayBuffer();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error ?? new Error('file read failed'));
    reader.readAsArrayBuffer(file);
  });
}

/** Serialize a FormData body to {@link OutboxMultipartBody} (file parts copied into raw bytes). */
async function serializeFormDataForOutbox(form: FormData): Promise<OutboxMultipartBody> {
  // `forEach` (not `entries()`) — this package's TS lib set has no DOM.Iterable.
  const raw: Array<readonly [string, FormDataEntryValue]> = [];
  form.forEach((value, name) => raw.push([name, value]));
  const parts: Array<readonly [string, string | OutboxFilePart]> = [];
  for (const [name, value] of raw) {
    parts.push(
      typeof value === 'string'
        ? [name, value]
        : [
            name,
            { __file: { name: value.name, type: value.type, bytes: await readFileBytes(value) } },
          ],
    );
  }
  return { __multipart: parts };
}

/** Narrow an outbox body to the serialized-FormData shape (see {@link OutboxMultipartBody}). */
export function isOutboxMultipartBody(body: unknown): body is OutboxMultipartBody {
  return (
    typeof body === 'object' &&
    body !== null &&
    Array.isArray((body as OutboxMultipartBody).__multipart)
  );
}

/** A queueable mutation captured from an axios config. */
export interface OutboxRequest {
  readonly method: string;
  readonly url: string;
  /**
   * Structured-cloneable request body: the JSON body (axios `data`), an {@link OutboxMultipartBody}
   * for a FormData write, or undefined for bodyless writes.
   */
  readonly body: unknown;
  /** The client-generated idempotency key the server dedupes on (and the replay re-sends). */
  readonly idempotencyKey: string;
  /**
   * The platform-owner impersonation scope (ADR-0055) ACTIVE WHEN THE WRITE WAS ENQUEUED — the
   * `X-Act-As-Household` target, or null when not impersonating. Captured here (not at replay time)
   * so an offline write replays under the household it was authored in, never under whatever scope
   * the owner happens to be acting in later. Replay re-attaches it and refuses an entry whose scope
   * no longer matches the live one.
   */
  readonly actAsHouseholdId: string | null;
}

export interface ApiAuthBridge {
  /** Current in-memory access token, or null when unauthenticated. */
  getAccessToken: () => string | null;
  /**
   * The readable CSRF token to echo as the `X-CSRF-Token` header (double-submit). It is RETURNED IN
   * THE BODY of `GET /v1/auth/csrf` and held in memory by the host — NOT read from `document.cookie`
   * (the cookie half is httpOnly and unreadable). Returns null until the host has fetched one.
   */
  getCsrfToken: () => string | null;
  /** Perform a single refresh round-trip; resolves to the new access token or null on failure. */
  refresh: () => Promise<string | null>;
  /** Called when refresh fails (clear session + redirect to login). */
  onAuthFailure: () => void;
  /**
   * Scoped impersonation target (ADR-0055): the household a PLATFORM OWNER is currently "acting in",
   * or null normally. When set, it is echoed as the `X-Act-As-Household` header on every request
   * except `/v1/auth/*` (own-identity routes — see the request interceptor); the
   * server honors it ONLY for an allowlisted owner (a non-owner's header is refused 403) and rewrites
   * the effective principal to a full admin in that household. Optional so non-owner builds/tests need
   * not provide it.
   */
  getActAsHouseholdId?: () => string | null;
}

let bridge: ApiAuthBridge | null = null;
let outbox: OutboxBridge | null = null;

/** Wire the auth bridge (called once by apps/web at startup). */
export function configureApiClient(next: ApiAuthBridge): void {
  bridge = next;
}

/** Wire the offline outbox bridge (called once by apps/web at startup). */
export function configureOutbox(next: OutboxBridge | null): void {
  outbox = next;
}

/** True for write methods whose offline failure we queue (GET/HEAD reads are handled by the SW). */
function isWriteMethod(method: string | undefined): boolean {
  const m = (method ?? 'get').toLowerCase();
  return m === 'post' || m === 'put' || m === 'patch' || m === 'delete';
}

/**
 * An axios error counts as "offline" (queue it) when the browser reports no connection OR the
 * request never got a response (network error / DNS / connection refused / abort-on-offline). A
 * request that DID get a response (any HTTP status) reached the server and must NOT be queued — the
 * server already saw it; queueing would risk a non-idempotent replay and hide real 4xx/5xx errors.
 */
function isOfflineError(error: AxiosError): boolean {
  if (!axios.isAxiosError(error)) return false;
  if (error.response) return false;
  // A caller/react-query cancellation is never an outage — must not be queued and faked as success, even
  // while the browser reports offline (the onLine short-circuit below would otherwise swallow it).
  if (error.code === 'ERR_CANCELED') return false;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;
  // No response + a KNOWN network-class axios code → treat as offline (covers `ERR_NETWORK`, timeouts).
  // We deliberately do NOT fall back to "any code-less error is offline": a non-Axios throw (a bad
  // interceptor, a programming error) has no code and would otherwise be swallowed into the outbox and
  // resolved as a fake success — masking a real failure and durably queuing a spurious write.
  return error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED';
}

/**
 * Per-request outbox opt-out (set by `idempotentNoOutbox` in the hook layer). A response-bearing
 * create (household provisioning, one-time reset/invite links) must FAIL LOUD offline: queueing it
 * would resolve `undefined` (the caller's `onSuccess` dereferences the response) while the replay
 * later performs the write with its one-time token never shown anywhere.
 */
function isOutboxExempt(config: AxiosRequestConfig): boolean {
  return (config as AxiosRequestConfig & { noOutbox?: boolean }).noOutbox === true;
}

/** Read the `Idempotency-Key` from an axios config's headers, whatever header container it uses. */
function readIdempotencyKey(config: AxiosRequestConfig | undefined): string | null {
  const headers = config?.headers as
    | { get?: (name: string) => unknown; [k: string]: unknown }
    | undefined;
  if (!headers) return null;
  // AxiosHeaders exposes `.get`; a plain object is indexed directly (case-insensitive scan).
  if (typeof headers.get === 'function') {
    const viaGet = headers.get('Idempotency-Key');
    if (viaGet != null) return String(viaGet);
  }
  for (const [name, value] of Object.entries(headers)) {
    if (name.toLowerCase() === 'idempotency-key' && value != null) return String(value);
  }
  return null;
}

/** Base URL from Vite env; falls back to the documented dev port. */
const baseURL =
  (typeof import.meta !== 'undefined' &&
    (import.meta as { env?: Record<string, string> }).env?.VITE_API_URL) ||
  'http://localhost:3000';

export const axiosInstance: AxiosInstance = axios.create({
  baseURL,
  withCredentials: true,
  // Serialize array query params as REPEATED keys (`reason=a&reason=b`), NOT axios's default bracket
  // form (`reason[]=a&reason[]=b`). The API runs on Express 5 whose default `simple` query parser does
  // NOT expand `reason[]` into an array — it keeps the literal key `reason[]`, which the strict
  // (`forbidNonWhitelisted`) validation pipe then rejects with a 400. Repeated keys parse into a real
  // array, matching the DTO's documented contract (a repeated `reason=` param) and the OpenAPI
  // form/explode convention. Scalars serialize as plain `key=value`; null/undefined are skipped.
  paramsSerializer: {
    serialize: (params: Record<string, unknown>): string => {
      const sp = new URLSearchParams();
      for (const [key, value] of Object.entries(params)) {
        if (value === undefined || value === null) continue;
        if (Array.isArray(value)) {
          for (const item of value) {
            if (item !== undefined && item !== null) sp.append(key, String(item));
          }
        } else {
          sp.append(key, String(value));
        }
      }
      return sp.toString();
    },
  },
});

/** Routes that carry the refresh cookie and therefore require the CSRF header. */
const CSRF_ROUTES = ['/v1/auth/refresh', '/v1/auth/logout', '/v1/auth/change-password'];

function needsCsrf(url: string | undefined): boolean {
  return !!url && CSRF_ROUTES.some((route) => url.includes(route));
}

/**
 * Serialize a config's axios `params` into a self-contained URL for the offline outbox. The queueable
 * writes use flat key→value params, so a plain URLSearchParams encoding matches axios's default serializer.
 * Nullish values are skipped (as axios does). Returns the URL unchanged when there are no params.
 */
function withQueryParams(url: string, params: unknown): string {
  if (!params || typeof params !== 'object') return url;
  const usp = new URLSearchParams();
  for (const [key, value] of Object.entries(params as Record<string, unknown>)) {
    if (value !== undefined && value !== null) usp.append(key, String(value));
  }
  const qs = usp.toString();
  if (!qs) return url;
  return url.includes('?') ? `${url}&${qs}` : `${url}?${qs}`;
}

axiosInstance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = bridge?.getAccessToken() ?? null;
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  // Scoped impersonation (ADR-0055): a platform owner "acting in" a household echoes the target on
  // every request. The server gates this on the live `isPlatformOwner` bit (a non-owner's header is a
  // hard 403), so it is safe to attach unconditionally when set. The `/v1/platform-admin/*` surface
  // ignores it (it is not household-scoped) but checks the same untouched owner bit, so it keeps working.
  // EXCEPT `/v1/auth/*`: those routes act on the owner's OWN identity (refresh/csrf/logout are @Public
  // or session-scoped, never household-scoped) — attaching the header there once 403'd the transparent
  // token refresh mid-impersonation and force-logged the owner out.
  const actAs = bridge?.getActAsHouseholdId?.() ?? null;
  // Do NOT clobber an explicitly-set scope header: the offline-outbox replay pins the household a queued
  // write was authored in (outbox-controller.replayOne), which must win over the live acting-as scope —
  // otherwise a mid-drain scope switch could re-stamp a replayed write into the wrong household.
  if (actAs && !config.url?.includes('/v1/auth/') && !config.headers.has('X-Act-As-Household')) {
    config.headers.set('X-Act-As-Household', actAs);
  }
  if (needsCsrf(config.url) && config.method && config.method.toLowerCase() !== 'get') {
    const csrf = bridge?.getCsrfToken() ?? null;
    if (csrf) {
      config.headers.set('X-CSRF-Token', csrf);
    }
  }
  return config;
});

/** Marks a request that has already been retried after a refresh, so we never loop. */
interface RetriableConfig extends InternalAxiosRequestConfig {
  _retried?: boolean;
}

let refreshInflight: Promise<string | null> | null = null;

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as RetriableConfig | undefined;
    const status = error.response?.status;
    const isRefreshCall = original?.url?.includes('/v1/auth/refresh');

    // Only attempt a transparent refresh on a 401 from a non-refresh call, once.
    if (status === 401 && original && !isRefreshCall && bridge) {
      if (!original._retried) {
        original._retried = true;
        // Coalesce concurrent 401s into a single refresh round-trip.
        refreshInflight ??= bridge.refresh().finally(() => {
          refreshInflight = null;
        });
        const newToken = await refreshInflight;
        if (newToken) {
          original.headers.set('Authorization', `Bearer ${newToken}`);
          return axiosInstance(original);
        }
        bridge.onAuthFailure();
      } else {
        // The already-refreshed retry itself 401'd — the freshly issued token is being rejected, so
        // there is no recovery path. Clear the session rather than leaving the app stuck re-401-ing.
        bridge.onAuthFailure();
      }
    }
    return Promise.reject(error);
  },
);

/**
 * The Orval mutator. Generated hooks call `apiFetch<T>(config)`; we return the response body typed
 * as `T`. An `AbortSignal` from react-query is forwarded for cancellation.
 */
export const apiFetch = async <T>(
  config: AxiosRequestConfig,
  options?: AxiosRequestConfig,
): Promise<T> => {
  // Deep-merge `headers` so per-call options (e.g. an `Idempotency-Key`) are ADDED to the generated
  // headers rather than replacing them — a shallow `{ ...config, ...options }` would drop the
  // generated `Content-Type` on idempotent writes. Other keys still take the override.
  const merged: AxiosRequestConfig = {
    ...config,
    ...options,
    ...(config.headers || options?.headers
      ? { headers: { ...config.headers, ...options?.headers } }
      : {}),
  };
  // MULTIPART: the generated client sets `Content-Type: multipart/form-data` with NO boundary on file
  // uploads (e.g. photo proof). Sent as-is, neither the browser nor the server can parse the parts.
  // For a FormData body we DROP the manual Content-Type so axios/the browser (or Node's form-data)
  // computes the header WITH the multipart boundary.
  if (typeof FormData !== 'undefined' && merged.data instanceof FormData && merged.headers) {
    for (const name of Object.keys(merged.headers)) {
      if (name.toLowerCase() === 'content-type') {
        delete (merged.headers as Record<string, unknown>)[name];
      }
    }
  }
  try {
    const response = await axiosInstance(merged);
    return response.data as T;
  } catch (error) {
    const axiosError = error as AxiosError;
    // OFFLINE OUTBOX (ADR-0018): when an idempotent write can't reach the network, hand it to the
    // app outbox and RESOLVE so the optimistic UI holds (the change is queued, not lost). Only writes
    // that carry an `Idempotency-Key` are queueable — the server dedupes the replay, so it's safe.
    // Exempt requests (see `isOutboxExempt`) propagate the network error instead.
    if (
      outbox &&
      !isOutboxExempt(merged) &&
      isWriteMethod(merged.method) &&
      isOfflineError(axiosError)
    ) {
      const idempotencyKey = readIdempotencyKey(merged);
      if (idempotencyKey && merged.url) {
        // A FormData body (photo-proof completion) must be serialized to a cloneable shape first —
        // IndexedDB structured-clone rejects FormData itself, so persisting it raw would throw
        // `DataCloneError` and DROP the write instead of queueing it. Replay rebuilds the FormData
        // (outbox-controller `replayOne`). A failed serialization (bytes unreadable) skips the queue
        // so the original network error propagates rather than enqueueing a broken write.
        let queuedBody: unknown = merged.data;
        let queueable = true;
        if (typeof FormData !== 'undefined' && merged.data instanceof FormData) {
          try {
            queuedBody = await serializeFormDataForOutbox(merged.data);
          } catch {
            queueable = false;
          }
        }
        const accepted =
          queueable &&
          (await outbox
            .enqueue({
              method: (merged.method ?? 'post').toUpperCase(),
              // Fold axios `params` into the stored URL: the outbox persists only method/url/body, and axios
              // serializes `params` into the query string at SEND time — so a param-bearing write (e.g. DELETE
              // /v1/learning/placement?childId&subject) would otherwise replay with its identifying params
              // STRIPPED → 400 → the offline write is permanently dropped. The URL is self-contained on replay.
              url: withQueryParams(merged.url, merged.params),
              body: queuedBody,
              idempotencyKey,
              // Snapshot the impersonation scope NOW so the replay lands in the household this write was
              // authored in, not whatever the owner is acting in by replay time.
              actAsHouseholdId: bridge?.getActAsHouseholdId?.() ?? null,
            })
            // A failing enqueue (e.g. IndexedDB QuotaExceeded/unavailable) must not replace the real network
            // error — fall through to `throw error` with the original AxiosError instead of a confusing IDB one.
            .catch(() => false));
        // The mutation is now durably queued; resolve as a void success (queueable mutations return
        // `void` or an id the caller can't use offline anyway — the optimistic cache update is the
        // source of UX truth until replay reconciles from the server; response-CONSUMING creates opt
        // out via `noOutbox` above so they never resolve `undefined`).
        if (accepted) return undefined as T;
      }
    }
    throw error;
  }
};
